import React, { useEffect, useMemo, useState } from 'react';
import Page from './components/Page';
import { getClassName } from '@onsolve/utils.get-class-name';
import styles from './ResponsePortal.module.scss';
import ResponsePortalService from '@services/responseportal.service';
import Question from './components/Question';
import SuccessToast from './components/SuccessToast';
import ErrorToast from './components/ErrorToast';
import ResponseSelection from './components/ResponseSelection';
import configurationService from '@services/config.service';
import { t } from '@lingui/macro';
import ShowResponses from './components/ShowResponses';
const ResponsePortal = ({ namespace }) => {
    const [rootClass, getChildClass] = getClassName({
        rootClass: 'responsePortal-page',
        styles,
    });
    const baseURL = configurationService.ResponseServiceUrl;
    const responseServiceClient = new ResponsePortalService(baseURL);
    const url = window.location.href;
    const urlParams = new URL(url).searchParams;
    const base64Data = urlParams.get('data');
    const decodedUrlParam = atob(base64Data);
    const queryParams1 = new URLSearchParams(decodedUrlParam);
    const alertId = queryParams1.get('scriptuuid');
    const deviceId = queryParams1.get('devicehistid');
    const [responseIndex, setResponseIndex] = useState(-1);
    const [response, setResponse] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [followUpAnswers, setFollowUpAnswers] = useState([]);
    const [currentFollowUps, setCurrentFollowUps] = useState(undefined);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [showQuestion, setShowQuestion] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [showFinalResponse, setShowFinalResponse] = useState(false);
    useEffect(() => {
        const GetAlertResponse = async () => {
            try {
                const data = await responseServiceClient.GetAlertResponses(alertId, deviceId);
                addSerialNumber(data);
                setResponse(data);
                setIsLoading(false);
                if (data?.settings?.confirmResponseRequired) {
                    setResponseIndex(Number(queryParams1.get('response') || 0));
                }
                else {
                    setResponseIndex(Number(queryParams1.get('response') || 0));
                    setIsDone(false);
                }
            }
            catch (error) {
                console.log(error);
                if (error.message.includes('404')) {
                    setShowErrorToast(true);
                    setErrorMessage(t `We regret to inform you that your response has not been accepted as the alert or the link has expired.`);
                }
            }
        };
        GetAlertResponse();
    }, [alertId, deviceId]);
    useEffect(() => {
        if (responseIndex > -1) {
            response['response'] = response?.responseOptions[responseIndex]?.id;
            setResponse(() => response);
            const followUps = response?.responseOptions[responseIndex]?.followUp;
            if (followUps?.length > 0) {
                setFollowUpAnswers(followUps || []);
                setCurrentFollowUps(followUps?.[0]);
                setIsDone(followUps?.length > 1 ? false : true);
                setShowQuestion(true);
            }
            else if (!response?.settings?.confirmResponseRequired) {
                saveAnswer(responseIndex);
            }
        }
    }, [responseIndex]);
    const addSerialNumber = (data) => {
        data?.responseOptions?.map((item, index) => {
            item.followUp?.map((followup, followupIndex) => {
                followup.serialNumber = followupIndex + 1;
            });
        });
    };
    const confirmResponse = (responseSelected) => {
        response?.responseOptions?.map((item, index) => {
            if (item.id === responseSelected) {
                setResponseIndex(index);
                if (response?.responseOptions[index]?.followUp?.length === 0) {
                    saveAnswer(index);
                }
            }
        });
        response['response'] = responseSelected;
        setResponse(() => response);
    };
    const changeResponse = () => {
        setShowFinalResponse(false);
        const followUps = response?.responseOptions[responseIndex]?.followUp;
        if (followUps?.length > 0) {
            setFollowUpAnswers(followUps || []);
            setCurrentFollowUps(followUps?.[0]);
            setIsDone(followUps?.length > 1 ? false : true);
        }
        if (response?.settings?.confirmResponseRequired) {
            setResponseIndex(-1);
            setShowQuestion(false);
        }
        else if (followUps?.length === 0) {
            window.location.reload();
        }
        else {
            setResponseIndex(Number(queryParams1.get('response') || 0));
            setShowQuestion(true);
        }
    };
    const isSaveBtnDisabled = useMemo(() => {
        return false;
    }, [followUpAnswers]);
    const saveResponse = (followup) => {
        const _followUpAnswers = [...followUpAnswers];
        _followUpAnswers.map((obj) => (obj.id === followup.id ? followup : obj));
        setFollowUpAnswers(_followUpAnswers);
        const currentIndex = _followUpAnswers.findIndex((item) => item.id === followup?.id);
        const nextFollowUp = currentIndex !== -1 && currentIndex < _followUpAnswers.length - 1
            ? _followUpAnswers[currentIndex + 1]
            : null;
        setCurrentFollowUps(nextFollowUp);
        const lastIndex = _followUpAnswers.length - 1;
        setIsDone(lastIndex > currentIndex + 1 ? false : true);
    };
    const saveAnswer = async (index) => {
        if (isSaveBtnDisabled)
            return;
        try {
            index = index === undefined ? responseIndex : index;
            const payload = {
                alertId: alertId,
                deviceId: deviceId,
                response: response.responseOptions[index]?.id,
                followUp: followUpAnswers,
                deviceType: 0,
            };
            setIsLoading(true);
            await responseServiceClient.PostAlertResponses(payload);
            setIsLoading(false);
            setShowSuccessToast(true);
            setShowFinalResponse(true);
        }
        catch (error) {
            console.log('error', error);
            setIsLoading(false);
            if (error?.message.includes('409')) {
                setShowErrorToast(true);
                setErrorMessage(t `Your response could not be accepted, because this notification has already been closed.`);
            }
        }
        return;
    };
    return (<Page className={`${rootClass}`} namespace={namespace}>
            {showSuccessToast && <SuccessToast setShowSuccessToast={setShowSuccessToast}/>}
            {showErrorToast && <ErrorToast setShowErrorToast={setShowErrorToast} message={errorMessage}/>}
            <div className={getChildClass('responseOptionPage')}>
                {response && response?.settings?.confirmResponseRequired && !showQuestion && !showFinalResponse && (<ResponseSelection response={response} isLoading={isLoading} confirmResponse={confirmResponse} isSaveBtnDisabled={false}/>)}
                {response && showQuestion && !showFinalResponse && (<Question key={response.responseOptions[responseIndex]?.id} saveAnswer={saveAnswer} saveResponse={saveResponse} isLoading={isLoading} currentFollowUps={currentFollowUps} isDone={isDone}/>)}
                {showFinalResponse && (<ShowResponses isLoading={isLoading} response={response} handleResponseChange={changeResponse}/>)}
            </div>
        </Page>);
};
export default ResponsePortal;
